import type { Card, Suit } from '@durakapp/engine';

export type HandSortMode = 'auto' | 'suit' | 'rank';

export const SORT_MODES: readonly HandSortMode[] = ['auto', 'suit', 'rank'];

const SUIT_ORDER: Record<Suit, number> = {
  spades: 0,
  clubs: 1,
  hearts: 2,
  diamonds: 3,
};

/**
 * Returns a *new* hand sorted according to the chosen mode. trumpSuit is consulted in every
 * mode so trumps stay visually grouped (rightmost) — that matches how Russian Дурак is
 * traditionally arranged in hand.
 *
 * Modes:
 *   auto  — non-trump cards by rank asc, then all trumps by rank asc. The default playable
 *           layout: lowest beat-card on the left, strongest trump on the right.
 *   suit  — grouped by suit (trump suit last), within each group by rank desc.
 *   rank  — by rank asc; equal ranks broken by suit (trump last).
 */
export function sortHand(cards: readonly Card[], trumpSuit: Suit, mode: HandSortMode): Card[] {
  const arr = cards.slice();
  switch (mode) {
    case 'auto':
      arr.sort((a, b) => {
        const aTrump = a.suit === trumpSuit ? 1 : 0;
        const bTrump = b.suit === trumpSuit ? 1 : 0;
        if (aTrump !== bTrump) return aTrump - bTrump;
        if (a.rank !== b.rank) return a.rank - b.rank;
        return SUIT_ORDER[a.suit] - SUIT_ORDER[b.suit];
      });
      return arr;
    case 'suit':
      arr.sort((a, b) => {
        const aTrump = a.suit === trumpSuit ? 1 : 0;
        const bTrump = b.suit === trumpSuit ? 1 : 0;
        if (aTrump !== bTrump) return aTrump - bTrump;
        if (a.suit !== b.suit) return SUIT_ORDER[a.suit] - SUIT_ORDER[b.suit];
        return b.rank - a.rank;
      });
      return arr;
    case 'rank':
      arr.sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        const aTrump = a.suit === trumpSuit ? 1 : 0;
        const bTrump = b.suit === trumpSuit ? 1 : 0;
        if (aTrump !== bTrump) return aTrump - bTrump;
        return SUIT_ORDER[a.suit] - SUIT_ORDER[b.suit];
      });
      return arr;
  }
}

export function nextSortMode(current: HandSortMode): HandSortMode {
  const i = SORT_MODES.indexOf(current);
  return SORT_MODES[(i + 1) % SORT_MODES.length] ?? 'auto';
}

export const SORT_LABELS: Record<HandSortMode, string> = {
  auto: 'Авто',
  suit: 'Масть',
  rank: 'Ранг',
};
